'use client';

import { useWallets } from '@privy-io/react-auth';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { mockTrades, mockUsers } from '@/data';
import { useAuth } from '@/hooks/useAuth';
import { useClobClient } from '@/hooks/useClobClient';
import { useT } from '@/hooks/useT';
import { leaderboardApi, marketsApi, portfolioApi } from '@/services/api';
import type { PortfolioSummary, Position, Trade } from '@/types';
import { publicClient } from '@/utils/approvals.utils';
import { deriveSafe } from '@/utils/polymarket.utils';

// Query Keys Factory
export const PORTFOLIO_QUERY_KEYS = {
  all: ['portfolio'] as const,
  bridgeDepositAddress: () => [...PORTFOLIO_QUERY_KEYS.all, 'bridge-deposit-address'] as const,
  positions: () => [...PORTFOLIO_QUERY_KEYS.all, 'positions'] as const,
  otherPositions: (user: string, eventId: string) =>
    [...PORTFOLIO_QUERY_KEYS.all, 'other-positions', user, eventId] as const,
  userValue: (user: string) => [...PORTFOLIO_QUERY_KEYS.all, 'user-value', user] as const,
  trades: () => [...PORTFOLIO_QUERY_KEYS.all, 'trades'] as const,
  summary: (proxyWallet?: string) =>
    [...PORTFOLIO_QUERY_KEYS.all, 'summary', proxyWallet ?? ''] as const,
  usdcBalance: (proxyWallet?: string) =>
    [...PORTFOLIO_QUERY_KEYS.all, 'usdc-balance', proxyWallet ?? ''] as const,
  pusdBalance: (proxyWallet?: string) =>
    [...PORTFOLIO_QUERY_KEYS.all, 'pusd-balance', proxyWallet ?? ''] as const,
  pnl: (user: string, interval: string, fidelity: string) =>
    [...PORTFOLIO_QUERY_KEYS.all, 'pnl', user, interval, fidelity] as const,
  sortCategories: (type: string) => [...PORTFOLIO_QUERY_KEYS.all, 'sort-categories', type] as const,
  actions: () => [...PORTFOLIO_QUERY_KEYS.all, 'actions'] as const,
  sortOptions: () => [...PORTFOLIO_QUERY_KEYS.all, 'sort-options'] as const,
  depositAddress: () => [...PORTFOLIO_QUERY_KEYS.all, 'deposit-address'] as const,
};

export const usePolymarketBridgeAddress = () => {
  const { user } = useAuth();
  return useQuery<{
    destinationAddress: string;
    addresses: { evm?: string; svm?: string; btc?: string; tvm?: string };
    raw?: any;
    minimums: { chainId: number; chainName: string; symbol: string; minCheckoutUsd: number }[];
  } | null>({
    queryKey: PORTFOLIO_QUERY_KEYS.bridgeDepositAddress(),
    queryFn: () => portfolioApi.getBridgeDepositAddress(),
    // Only fetch once the proxy wallet is known — avoids 401/404 for unauthenticated users
    // and prevents an unnecessary API call on every modal mount before auth resolves.
    enabled: !!user?.proxyWalletAddress,
    staleTime: Infinity,
  });
};

export const useDepositAddress = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: PORTFOLIO_QUERY_KEYS.depositAddress(),
    queryFn: () => portfolioApi.getDepositAddress(),
    enabled: !!user?.proxyWalletAddress,
    staleTime: Infinity, // Address shouldn't change
  });
};

// Fetch activity actions (filter types)
export const usePortfolioActions = () => {
  return useQuery({
    queryKey: PORTFOLIO_QUERY_KEYS.actions(),
    queryFn: () => portfolioApi.getActions(),
    staleTime: Infinity,
  });
};

// Fetch activity sort options
export const usePortfolioSortOptions = () => {
  return useQuery({
    queryKey: PORTFOLIO_QUERY_KEYS.sortOptions(),
    queryFn: () => portfolioApi.getSortOptions(),
    staleTime: Infinity,
  });
};

// Fetch sort categories for active or closed positions
export const usePortfolioSortCategories = (type: 'active-positions' | 'closed-positions') => {
  const { language } = useT();
  return useQuery({
    queryKey: [...PORTFOLIO_QUERY_KEYS.sortCategories(type), language.code],
    queryFn: () => portfolioApi.getCategories(),
    staleTime: Infinity,
  });
};

// Fetch user positions
export const usePositions = (
  user?: string,
  eventId?: string,
  limit: number = 500,
  offset: number = 0,
  // Opt-out for callers that are mounted permanently but only need the data
  // some of the time (e.g. a closed modal). The query key is unchanged, so a
  // disabled caller still reads whatever another caller has already cached.
  options?: { enabled?: boolean },
) => {
  const clobClient = useClobClient();
  return useQuery<any[]>({
    queryKey: [...PORTFOLIO_QUERY_KEYS.positions(), user, eventId, limit, offset],
    queryFn: async () => {
      if (!user) return [];
      const [positions, openOrders] = await Promise.all([
        portfolioApi.getPositions(user, eventId, limit, offset),
        clobClient ? clobClient.getOpenOrders().catch(() => []) : Promise.resolve([]),
      ]);

      const ordersArray = Array.isArray(openOrders) ? openOrders : (openOrders as any).data || [];
      const lockedSharesMap = new Map<string, number>();
      ordersArray.forEach((order: any) => {
        if (order.side?.toUpperCase() === 'SELL') {
          const tokenId = order.asset_id;
          const size = parseFloat(order.original_size || '0');
          lockedSharesMap.set(tokenId, (lockedSharesMap.get(tokenId) || 0) + size);
        }
      });

      const positionsArray = Array.isArray(positions) ? positions : positions?.data || [];

      return positionsArray.map((p: any) => {
        const tokenId = p.asset || p.tokenId;
        const locked = lockedSharesMap.get(tokenId) || 0;
        const currentSize = parseFloat(p.size || p.shares || '0');
        return {
          ...p,
          totalSize: currentSize,
          size: Math.max(0, currentSize - locked).toString(),
          shares: Math.max(0, currentSize - locked).toString(),
        };
      });
    },
    // Back off to 30s while the endpoint is erroring instead of hammering it every 10s.
    refetchInterval: (query) => (query.state.error ? 30000 : 10000),
    enabled: !!user && (options?.enabled ?? true),
  });
};

// Fetch user positions as infinite query
export const useInfinitePositions = (
  user?: string,
  eventId?: string,
  limit: number = 20,
  title?: string,
  sortBy?: string,
) => {
  return useInfiniteQuery<any[]>({
    queryKey: [...PORTFOLIO_QUERY_KEYS.positions(), user, eventId, limit, title, sortBy],
    queryFn: async ({ pageParam = 0 }) => {
      if (!user) return [];
      const [response] = await Promise.all([
        portfolioApi.getPositions(user, eventId, limit, pageParam as number, title, sortBy),
      ]);

      const positionsArray = Array.isArray(response) ? response : response?.data || [];

      return positionsArray;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length < limit) return undefined;
      return allPages.length * limit;
    },
    initialPageParam: 0,
    enabled: !!user,
    refetchInterval: (query) => {
      // Back off to 30s while the endpoint is erroring instead of hammering it every 5s.
      if (query.state.error) return 30000;

      const pages = query.state.data?.pages;
      if (pages) {
        const hasIncompleteData = pages.flat().some((p: any) => {
          // If the user has shares but the backend hasn't calculated the avgPrice (0.0) yet
          const shares = parseFloat(p.size || p.shares || '0');
          const avgPrice = parseFloat(p.avgPrice || '0');
          return shares > 0 && avgPrice === 0;
        });
        if (hasIncompleteData) {
          return 1000; // Poll very fast to clear the 0.0¢ delay instantly
        }
      }
      return 5000; // Normal polling rate
    },
  });
};

// Fetch user 'other' positions/activity for an event
export const useOtherPositions = (user?: string, eventId?: string) => {
  return useQuery<any>({
    queryKey: PORTFOLIO_QUERY_KEYS.otherPositions(user || '', eventId || ''),
    queryFn: async () => {
      if (!user || !eventId) return null;
      return portfolioApi.getOtherPositions(user, eventId);
    },
    enabled: !!user && !!eventId,
  });
};

// Fetch total portfolio value for a user
export const usePortfolioUserValue = (user?: string) => {
  return useQuery<any>({
    queryKey: PORTFOLIO_QUERY_KEYS.userValue(user || ''),
    queryFn: async () => {
      if (!user) return null;
      return portfolioApi.getUserValue(user);
    },
    enabled: !!user,
  });
};

// Fetch user trade history
export const useTradeHistory = (
  user?: string,
  limit: number = 100,
  offset: number = 0,
  title?: string,
  sortDirection?: string,
  sortBy?: string,
  type?: string,
  start?: number,
  end?: number,
) => {
  return useQuery<any[]>({
    queryKey: [
      ...PORTFOLIO_QUERY_KEYS.trades(),
      user,
      limit,
      offset,
      title,
      sortDirection,
      sortBy,
      type,
      start,
      end,
    ],
    queryFn: async () => {
      if (!user) return [];
      const response = await portfolioApi.getHistory(
        user,
        limit,
        offset,
        title,
        sortDirection,
        sortBy,
        type,
        start,
        end,
      );
      return Array.isArray(response) ? response : response?.data || [];
    },
    enabled: !!user,
  });
};

// Fetch user trade history as infinite query
export const useInfiniteTradeHistory = (
  user?: string,
  limit: number = 20,
  title?: string,
  sortDirection?: string,
  sortBy?: string,
  type?: string,
  start?: number,
  end?: number,
  side?: string,
) => {
  return useInfiniteQuery<any[]>({
    queryKey: [
      ...PORTFOLIO_QUERY_KEYS.trades(),
      user,
      limit,
      title,
      sortDirection,
      sortBy,
      type,
      start,
      end,
      side,
    ],
    queryFn: async ({ pageParam = 0 }) => {
      if (!user) return [];
      const response = await portfolioApi.getHistory(
        user,
        limit,
        pageParam as number,
        title,
        sortDirection,
        sortBy,
        type,
        start,
        end,
        side,
      );
      return Array.isArray(response) ? response : response?.data || [];
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length < limit) return undefined;
      return allPages.length * limit;
    },
    initialPageParam: 0,
    enabled: !!user,
    refetchInterval: 30000,
  });
};

// Fetch user PnL history
export const usePrivyPolBalance = () => {
  const { wallets } = useWallets();
  const { user } = useAuth();
  const embeddedWallet = wallets.find((w) => w.walletClientType === 'privy');
  const address = embeddedWallet?.address ?? user?.walletAddress;

  return useQuery<number>({
    queryKey: ['privy-pol-balance', address],
    queryFn: async () => {
      if (!address) return 0;
      const balanceWei = await publicClient.getBalance({ address: address as `0x${string}` });
      return Number(balanceWei) / 1e18;
    },
    enabled: !!address,
    refetchInterval: 15000,
  });
};
export const usePortfolioUserPnl = (
  user?: string,
  interval: string = '1m',
  fidelity: string = '1d',
) => {
  return useQuery<any>({
    queryKey: PORTFOLIO_QUERY_KEYS.pnl(user || '', interval, fidelity),
    queryFn: async () => {
      if (!user) return null;
      return marketsApi.getUserPnl(user, interval, fidelity);
    },
    enabled: !!user,
    staleTime: 60000,
  });
};

/**
 * Fetch live USDC balance for the user's proxy wallet on Polygon.
 * This is the "available balance" shown in the portfolio.
 * Now unifies with the standard usePolygonBalances hook.
 */
export const useUsdcBalance = () => {
  const { user } = useAuth();
  const { wallets } = useWallets();
  const proxyWalletAddress = user?.proxyWalletAddress;

  const linkedWallet = wallets.find(
    (w) => w.address?.toLowerCase() === user?.walletAddress?.toLowerCase(),
  );
  const activeAddress = user?.walletAddress || linkedWallet?.address || wallets[0]?.address;

  const shouldDeriveSafeFallback = user?.tradingWalletKind !== 'deposit_wallet';
  const effectiveProxy =
    proxyWalletAddress ||
    (shouldDeriveSafeFallback && activeAddress ? deriveSafe(activeAddress) : undefined);

  const clobClient = useClobClient();

  // V2: Trading uses pUSD. We return the consolidated "available" balance
  // which will eventually be all pUSD after auto-migration.
  return useQuery<number>({
    queryKey: PORTFOLIO_QUERY_KEYS.usdcBalance(effectiveProxy),
    queryFn: async () => {
      if (!effectiveProxy) return 0;

      const [usdc, pusd, openOrders] = await Promise.all([
        portfolioApi.getLegacyUsdcBalance(effectiveProxy),
        portfolioApi.getPusdBalance(effectiveProxy),
        clobClient ? clobClient.getOpenOrders().catch(() => []) : Promise.resolve([]),
      ]);

      const ordersArray = Array.isArray(openOrders) ? openOrders : (openOrders as any).data || [];
      const lockedCash = ordersArray.reduce((acc: number, order: any) => {
        // Only BUY orders lock cash balance
        if (order.side?.toUpperCase() === 'BUY') {
          return acc + parseFloat(order.price || '0') * parseFloat(order.original_size || '0');
        }
        return acc;
      }, 0);

      return Math.max(0, usdc + pusd - lockedCash);
    },
    enabled: !!effectiveProxy,
    staleTime: 3000,
    refetchInterval: 5000,
  });
};

/**
 * Fetch live pUSD balance (v2 collateral).
 */
export const usePusdBalance = () => {
  const { user } = useAuth();
  const { wallets } = useWallets();
  const proxyWalletAddress = user?.proxyWalletAddress;

  const linkedWallet = wallets.find(
    (w) => w.address?.toLowerCase() === user?.walletAddress?.toLowerCase(),
  );
  const activeAddress = user?.walletAddress || linkedWallet?.address || wallets[0]?.address;

  const shouldDeriveSafeFallback = user?.tradingWalletKind !== 'deposit_wallet';
  const effectiveProxy =
    proxyWalletAddress ||
    (shouldDeriveSafeFallback && activeAddress ? deriveSafe(activeAddress) : undefined);

  return useQuery<number>({
    queryKey: PORTFOLIO_QUERY_KEYS.pusdBalance(effectiveProxy),
    queryFn: async () => {
      if (!effectiveProxy) return 0;
      return portfolioApi.getPusdBalance(effectiveProxy);
    },
    enabled: !!effectiveProxy,
    staleTime: 2000,
    refetchInterval: 3000,
  });
};

// Fetch portfolio summary — totalValue = positions value (data-api) + cash (on-chain USDC)
export const usePortfolioSummary = () => {
  const { user } = useAuth();
  const { wallets } = useWallets();
  const proxyWallet = user?.proxyWalletAddress;

  const linkedWallet = wallets.find(
    (w) => w.address?.toLowerCase() === user?.walletAddress?.toLowerCase(),
  );
  const activeAddress = user?.walletAddress || linkedWallet?.address || wallets[0]?.address;
  const shouldDeriveSafeFallback = user?.tradingWalletKind !== 'deposit_wallet';
  const effectiveProxy =
    proxyWallet ||
    (shouldDeriveSafeFallback && activeAddress ? deriveSafe(activeAddress) : undefined);

  const clobClient = useClobClient();

  const query = useQuery<PortfolioSummary>({
    queryKey: PORTFOLIO_QUERY_KEYS.summary(effectiveProxy),
    queryFn: async () => {
      if (!effectiveProxy) {
        return {
          totalValue: 0,
          totalPnl: 0,
          totalPnlPercent: 0,
          positionsCount: 0,
          openOrdersCount: 0,
          cashBalance: 0,
          pusdBalance: 0,
          legacyUsdcBalance: 0,
          nativeUsdcBalance: 0,
          requiresMigration: false,
          isPlaceholder: true,
        } as unknown as PortfolioSummary;
      }

      // Fetch balances and value in parallel.
      // cashBalance = proxy wallet only — these are the funds the CLOB can actually trade.
      // (EOA balance is tracked separately and auto-migrated to proxy by
      //  useEmbeddedWalletMigration before appearing here.)
      // Native USDC (0x3c499) cannot be used for Polymarket trading — the
      // CollateralOnramp only accepts USDC.e. It is tracked separately so the
      // UI can show a "needs conversion" notice, but it does NOT count toward
      // the tradeable cashBalance.
      const [legacyUsdcBalance, pusdBalance, nativeUsdcBalance, valueData, openOrders] =
        await Promise.all([
          portfolioApi.getLegacyUsdcBalance(effectiveProxy),
          portfolioApi.getPusdBalance(effectiveProxy),
          portfolioApi.getNativeUsdcBalance(effectiveProxy),
          portfolioApi.getUserValue(effectiveProxy).catch(() => null),
          clobClient ? clobClient.getOpenOrders().catch(() => []) : Promise.resolve([]),
        ]);

      // data-api returns: [{ user: '0x...', value: 0.9993 }] or { totalValue: ... }
      const positionsValue: number = valueData?.[0]?.value ?? valueData?.totalValue ?? 0;

      const ordersArray = Array.isArray(openOrders) ? openOrders : (openOrders as any).data || [];
      const lockedCash = ordersArray.reduce((acc: number, order: any) => {
        if (order.side?.toUpperCase() === 'BUY') {
          return acc + parseFloat(order.price || '0') * parseFloat(order.original_size || '0');
        }
        return acc;
      }, 0);

      // cashBalance = only CLOB-tradeable funds (USDC.e + pUSD) MINUS locked cash from open orders.
      // nativeUsdcBalance is exposed separately for UI info but excluded from trading balance.
      const tradeableCash = Math.max(0, legacyUsdcBalance + pusdBalance - lockedCash);
      const totalValue = positionsValue + lockedCash;

      return {
        totalValue,
        totalPnl: 0,
        totalPnlPercent: 0,
        positionsCount: 0,
        openOrdersCount: ordersArray.length,
        cashBalance: tradeableCash,
        pusdBalance,
        legacyUsdcBalance,
        nativeUsdcBalance,
        positionsValue,
        requiresMigration: legacyUsdcBalance > 0.01,
      } as PortfolioSummary;
    },
    enabled: !!effectiveProxy,
    // This query is mounted once in MainLayout (the root layout), so it polls on
    // every single page for every logged-in user regardless of whether they're
    // looking at a balance anywhere — 5 bundled API calls every 5s adds up fast.
    // A 5s interval isn't actually load-bearing for "see my balance update after
    // I trade": useClobOrder.ts's updateCacheAfterTrade() already explicitly
    // invalidates + refetches this exact query (PORTFOLIO_QUERY_KEYS.all) at
    // 0ms/1.5s/4s right after every trade, independent of this interval. This
    // background poll only needs to catch out-of-band changes (e.g. an external
    // deposit landing) while the user is idle, which doesn't need to be 5s-fresh.
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  return {
    ...query,
    effectiveProxy,
  };
};

// Fetch Polymarket geoblock status for the current user's IP
export const useGeoblockStatus = () => {
  return useQuery<{ blocked: boolean; ip: string; country: string; region: string } | null>({
    queryKey: ['geoblock'],
    queryFn: () => portfolioApi.getGeoblockStatus(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};
